import React from 'react';
import type { MenuProps } from 'antd';
import {
  HomeOutlined,
  ShopOutlined,
  AppstoreOutlined,
  TeamOutlined,
  BarChartOutlined,
  SettingOutlined,
  DollarOutlined,
  OrderedListOutlined,
  ScheduleOutlined,
  ShoppingOutlined,
  ToolOutlined,
  FundOutlined,
  LineChartOutlined,
  PieChartOutlined,
  UsergroupAddOutlined,
  NotificationOutlined,
  PlusCircleOutlined,
  UnorderedListOutlined,
  WalletOutlined,
  SmileOutlined,
  FileProtectOutlined,
  AccountBookOutlined,
  QrcodeOutlined,
  PictureOutlined,
  SafetyCertificateOutlined,
  PayCircleOutlined,
  MedicineBoxOutlined,
} from '@ant-design/icons';

export type MenuItem = Required<MenuProps>['items'][number];

export const LIVE_PET_MENU_ROOT_KEY = '/service/live-animal-menu';
export const LIVE_PET_LIST_MENU_KEY = '/service/live-animal?tab=list';
export const LIVE_PET_INCOMING_MENU_KEY = '/service/live-animal?tab=incoming';
export const LIVE_PET_HISTORY_MENU_KEY = '/service/live-animal?tab=history';

export const livePetSubMenuItems: MenuItem[] = [
  { key: LIVE_PET_LIST_MENU_KEY, label: '活体列表' },
  { key: LIVE_PET_INCOMING_MENU_KEY, label: '待接收' },
  { key: LIVE_PET_HISTORY_MENU_KEY, label: '迁移记录' },
];

export const menuIconMap: Record<string, React.ReactNode> = {
  HomeOutlined: <HomeOutlined />,
  ShopOutlined: <ShopOutlined />,
  AppstoreOutlined: <AppstoreOutlined />,
  TeamOutlined: <TeamOutlined />,
  BarChartOutlined: <BarChartOutlined />,
  SettingOutlined: <SettingOutlined />,
  DollarOutlined: <DollarOutlined />,
  OrderedListOutlined: <OrderedListOutlined />,
  ScheduleOutlined: <ScheduleOutlined />,
  ShoppingOutlined: <ShoppingOutlined />,
  ToolOutlined: <ToolOutlined />,
  FundOutlined: <FundOutlined />,
  LineChartOutlined: <LineChartOutlined />,
  PieChartOutlined: <PieChartOutlined />,
  UsergroupAddOutlined: <UsergroupAddOutlined />,
  NotificationOutlined: <NotificationOutlined />,
  PlusCircleOutlined: <PlusCircleOutlined />,
  UnorderedListOutlined: <UnorderedListOutlined />,
  WalletOutlined: <WalletOutlined />,
  SmileOutlined: <SmileOutlined />,
  FileProtectOutlined: <FileProtectOutlined />,
  AccountBookOutlined: <AccountBookOutlined />,
  QrcodeOutlined: <QrcodeOutlined />,
  PictureOutlined: <PictureOutlined />,
  SafetyCertificateOutlined: <SafetyCertificateOutlined />,
  PayCircleOutlined: <PayCircleOutlined />,
  MedicineBoxOutlined: <MedicineBoxOutlined />,
};

/**
 * 门店管理端侧边栏菜单配置
 */
export const menuItems: MenuItem[] = [
  {
    key: '/dashboard',
    icon: <HomeOutlined />,
    label: '经营概况',
  },
  {
    key: '/business',
    icon: <ShopOutlined />,
    label: '店面业务',
    children: [
      { key: '/business/cashier', icon: <DollarOutlined />, label: '收银台' },
      { key: '/business/orders', icon: <OrderedListOutlined />, label: '订单管理' },
      { key: '/business/mall-orders', icon: <ShoppingOutlined />, label: '商城订单' },
      { key: '/business/wash-orders', icon: <ScheduleOutlined />, label: '洗护订单' },
      { key: '/business/boarding-orders', icon: <SmileOutlined />, label: '寄养订单' },
    ],
  },
  {
    key: '/contract',
    icon: <WalletOutlined />,
    label: '资金结算',
    children: [
      { key: '/contract/orders', icon: <FileProtectOutlined />, label: '签约订单' },
      { key: '/contract/settlement', icon: <AccountBookOutlined />, label: '结算管理' },
      { key: '/contract/payroll', icon: <PayCircleOutlined />, label: '工资管理' },
    ],
  },
  {
    key: '/customer',
    icon: <TeamOutlined />,
    label: '客户中心',
    children: [
      { key: '/customer/members', icon: <TeamOutlined />, label: '会员管理' },
      { key: '/customer/balance-logs', icon: <WalletOutlined />, label: '余额流水' },
    ],
  },
  {
    key: '/product',
    icon: <AppstoreOutlined />,
    label: '商品管理',
    children: [
      { key: '/product/goods', icon: <ShoppingOutlined />, label: '商品列表' },
      { key: '/product/goods/create', icon: <PlusCircleOutlined />, label: '创建商品' },
      { key: '/product/category', icon: <UnorderedListOutlined />, label: '商品分类' },
    ],
  },
  {
    key: '/material',
    icon: <PictureOutlined />,
    label: '素材管理',
    children: [
      { key: '/material/list', icon: <PictureOutlined />, label: '素材列表' },
    ],
  },
  {
    key: '/service',
    icon: <ToolOutlined />,
    label: '服务管理',
    children: [
      { key: '/service/product', icon: <ToolOutlined />, label: '服务管理' },
    ],
  },
  {
    key: LIVE_PET_MENU_ROOT_KEY,
    icon: <SmileOutlined />,
    label: '活体管理',
    children: livePetSubMenuItems,
  },
  {
    key: '/statistics',
    icon: <BarChartOutlined />,
    label: '数据统计',
    children: [
      { key: '/statistics/business', icon: <FundOutlined />, label: '营业报表' },
      { key: '/statistics/sales', icon: <LineChartOutlined />, label: '销售报表' },
      { key: '/statistics/members', icon: <PieChartOutlined />, label: '会员报表' },
    ],
  },
  {
    key: '/settings',
    icon: <SettingOutlined />,
    label: '基础配置',
    children: [
      { key: '/settings/store', icon: <ShopOutlined />, label: '店铺管理' },
      { key: '/settings/staff', icon: <UsergroupAddOutlined />, label: '店员管理' },
      { key: '/settings/message', icon: <NotificationOutlined />, label: '消息推送' },
      { key: '/settings/store-code', icon: <QrcodeOutlined />, label: '门店码' },
      { key: '/settings/permission', icon: <SafetyCertificateOutlined />, label: '权限配置' },
    ],
  },
];

/**
 * 根据当前路径计算菜单选中项
 */
export function getSelectedKeys(pathname: string, search = ''): string[] {
  if (pathname === '/') return ['/dashboard'];
  if (pathname === '/service/live-animal') {
    const tab = new URLSearchParams(search).get('tab');
    if (tab === 'incoming') return [LIVE_PET_INCOMING_MENU_KEY];
    if (tab === 'history') return [LIVE_PET_HISTORY_MENU_KEY];
    return [LIVE_PET_LIST_MENU_KEY];
  }
  if (pathname.startsWith('/business/orders/')) return ['/business/orders'];
  if (pathname.startsWith('/business/medical/')) return ['/business/medical'];
  if (pathname.startsWith('/product/goods/edit/')) return ['/product/goods'];
  if (pathname.startsWith('/customer/members/create')) return ['/customer/members'];
  if (pathname.startsWith('/customer/members/detail/')) return ['/customer/members'];
  return [pathname];
}

/**
 * 根据当前路径计算需要展开的父菜单
 */
export function getOpenKeys(pathname: string): string[] {
  if (pathname === '/service/live-animal') return [LIVE_PET_MENU_ROOT_KEY];
  if (pathname.startsWith('/service/')) return ['/service'];
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length >= 2) {
    return [`/${parts[0]}`];
  }
  return [];
}
